/**
 * Which slice of production to pull: a preset (last hour / 24 hours / 7 days /
 * 30 days, or "since the last run" when there was one) as one row of chips, or
 * a custom range on a calendar with a time for each end. The range picked is
 * always spelled out underneath. Controlled: the parent keeps the
 * `PeriodChoice` and reads the ISO range with `choiceRange`.
 */

import { CalendarRange } from "lucide-react";
import { RangeCalendar, dayKey } from "@/components/ui/range-calendar";
import { formatDateTime } from "@/lib/format";
import { PERIODS, periodRange, toLocalInput, type PeriodKey } from "./period";

export interface PeriodChoice {
  key: PeriodKey | "since";
  custom: { start: string; end: string };
}

export const newPeriodChoice = (since?: string | null): PeriodChoice => {
  const now = new Date();
  return {
    key: since ? "since" : "24h",
    custom: { start: toLocalInput(new Date(now.getTime() - 86_400_000)), end: toLocalInput(now) },
  };
};

/** The ISO range picked, or null while a custom range is not valid. */
export function choiceRange(
  choice: PeriodChoice,
  since?: string | null,
  now = new Date(),
): { start: string; end: string } | null {
  if (choice.key === "since")
    return since && new Date(since) < now ? { start: since, end: now.toISOString() } : null;
  return periodRange(choice.key, now, choice.custom);
}

/** "45 min", "24 h", "7 days", "2 days 6 h". */
function span(range: { start: string; end: string }): string {
  const minutes = Math.round((Date.parse(range.end) - Date.parse(range.start)) / 60_000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h`;
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  return rest ? `${days} days ${rest} h` : `${days} days`;
}

export function PeriodPicker({
  value,
  onChange,
  since,
  id = "period",
}: {
  value: PeriodChoice;
  onChange: (v: PeriodChoice) => void;
  /** Where the last run's period ended; offers "Since the last run". */
  since?: string | null;
  id?: string;
}) {
  const presets = [
    ...(since ? [{ value: "since" as const, label: "Since the last run", short: "Since last run" }] : []),
    ...PERIODS.map((p) => ({ value: p.value, label: p.label, short: p.short })),
  ];
  const range = choiceRange(value, since);
  const pick = (key: PeriodChoice["key"]) => {
    // Custom starts from the days of the range on screen, as whole days.
    if (key === "custom" && range) {
      const day = (iso: string) => toLocalInput(new Date(iso)).slice(0, 10);
      return onChange({ key, custom: wholeDays(day(range.start), day(range.end)) });
    }
    onChange({ ...value, key });
  };
  return (
    <div className="flex flex-col gap-2.5">
      <span id={`${id}-label`} className="text-[13px] font-semibold">
        Traces from
      </span>
      <div role="radiogroup" aria-labelledby={`${id}-label`} className="flex flex-wrap gap-1.5">
        {presets.map((p) => {
          const on = value.key === p.value;
          return (
            <button
              key={p.value}
              type="button"
              role="radio"
              aria-checked={on}
              aria-label={p.label}
              onClick={() => pick(p.value)}
              className={`h-7 rounded-full border px-2.5 text-xs font-medium transition-colors ${
                on ? "border-primary bg-primary text-on-primary" : "border-input text-body hover:bg-subtle"
              }`}
            >
              {p.short}
            </button>
          );
        })}
      </div>
      {value.key === "custom" && <CustomRange id={id} value={value} onChange={onChange} />}
      <p
        role={range ? "status" : "alert"}
        className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs ${range ? "bg-subtle text-body" : "bg-err-bg text-err"}`}
      >
        <CalendarRange aria-hidden className="size-3.5 shrink-0" />
        {range ? (
          <>
            <span className="tabular-nums">
              {formatDateTime(range.start)} → {formatDateTime(range.end)}
            </span>
            <span className="ml-auto text-muted">{span(range)}</span>
          </>
        ) : (
          "The end must come after the start."
        )}
      </p>
    </div>
  );
}

/** Picked days as whole days: from the first day's start to the last day's end,
 *  or to now when the last day is today. Times can then be narrowed by hand. */
function wholeDays(first: string, last: string): { start: string; end: string } {
  const now = toLocalInput(new Date());
  return { start: `${first}T00:00`, end: last === now.slice(0, 10) ? now : `${last}T23:59` };
}

function CustomRange({
  id,
  value,
  onChange,
}: {
  id: string;
  value: PeriodChoice;
  onChange: (v: PeriodChoice) => void;
}) {
  const { start, end } = value.custom;
  const set = (next: { start: string; end: string }) => onChange({ ...value, custom: next });
  const times = [
    { key: "start" as const, label: "From", day: start.slice(0, 10), time: start.slice(11, 16) },
    { key: "end" as const, label: "To", day: end.slice(0, 10), time: end.slice(11, 16) },
  ];
  return (
    <div className="flex gap-5 rounded-[9px] border border-line p-3.5">
      <RangeCalendar
        start={start.slice(0, 10)}
        end={end.slice(0, 10)}
        max={dayKey(new Date())}
        onChange={(a, b) => set(wholeDays(a, b))}
      />
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-4 border-l border-line-soft pl-5">
        {times.map((t) => (
          <label key={t.key} htmlFor={`${id}-${t.key}`} className="flex min-w-0 flex-col gap-1">
            <span className="text-[11px] font-semibold tracking-[0.05em] text-faint uppercase">
              {t.label}
            </span>
            <span className="truncate text-[13px] font-medium">
              {new Date(`${t.day}T00:00`).toLocaleDateString(undefined, {
                weekday: "short",
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </span>
            <input
              id={`${id}-${t.key}`}
              type="time"
              aria-label={`${t.label} time`}
              className="inp w-full font-mono"
              value={t.time}
              onChange={(e) =>
                e.target.value && set({ ...value.custom, [t.key]: `${t.day}T${e.target.value}` })
              }
            />
          </label>
        ))}
      </div>
    </div>
  );
}
